<!-- View controlling the feature list for a specific ad. This is routed from ad-list.vue -->
<template>
  <v-card>
    <v-container v-if="authorized" fluid class="ma-0 pa-0" v-resize="onResize">
      <v-toolbar color="fill">
        <v-breadcrumbs :items="breadcrumbs">
          <template #item="{ item }">
            <v-breadcrumbs-item :to="item.to" :disabled="item.disabled" link>
              <span :class="!item.disabled ? 'primary--text' : ''">{{ item.text }}</span>
            </v-breadcrumbs-item>
          </template>
        </v-breadcrumbs>
        <!-- <v-btn :to="'/ads'" :class="body" text class="accent--text text-capitalize">
          <v-icon left>mdi-chevron-left</v-icon>
          {{adParty.name}} ({{selectedAd.ad_name}})
        </v-btn> -->
        <v-spacer />
        <v-col cols="auto" v-if="selectedFeatures.length > 0">
          <v-btn color="primary" @click.stop="showMassCopy = true">
            Copy Features ({{ selectedFeatures.length }})
          </v-btn>
        </v-col>
        <v-col cols="3" class="pl-0">
          <v-text-field 
            :class="body" 
            label="Filter Features" 
            placeholder="Filter By Position or Text" 
            class="mt-7"
            prepend-inner-icon="mdi-filter-outline" 
            outlined 
            dense 
            background-color="input" 
            v-model="search"
            clearable>
          </v-text-field>
        </v-col>
        <div class="text-center">
          <AdActions :ad="selectedAd" :adParty="adParty">
            <template v-slot:default="{ on, attrs }">
              <v-btn color="primary" dark v-bind="attrs" v-on="on">
                <v-icon>mdi-dots-vertical</v-icon>
              </v-btn>
            </template>
          </AdActions>
        </div>
        <!-- <v-btn text :class="body" color="primary" @click="component='FeatureInfo', modal=true">
          <v-icon left>mdi-plus</v-icon>Create
        </v-btn> -->
      </v-toolbar>
      <v-divider />
      <v-tabs background-color="white" grow height="35" hide-slider v-model="pageIndex">
        <v-tab v-for="(page, index) in pages" :key="index" @change="getAdPages(page)"
          class="text-capitalize">
          {{ page.name }}
        </v-tab>
      </v-tabs>
      <v-data-table 
        v-model="selectedFeatures"
        :footer-props="{ 'items-per-page-options': pageSizes }"
        :headers="featureHeaders" 
        :height="tableHeight" 
        :items-per-page="pageSize" 
        :items="adFeatures"
        :loading="loading" 
        style="cursor:pointer" 
        :class="table" 
        dense 
        :search.sync="search"
        fixed-header 
        item-key="id"
        :show-select="($auth.tenant === 'alliance-retail-group')"
        loading-text="Loading Features... Please Wait" 
        no-data-text="No Features to Display"
        no-results-text="No Matching Features Found" 
        :sort-by="sortBy" 
        :sort-desc="false"
        @click:row="viewFeature">
        <template #[`item.promo_item_group_ids`]="{ item }">
          {{ item.promo_item_group_ids.length ? item.promo_item_group_ids.length : 0  }}
        </template>
        <template #[`item.non_promo_items`]="{ item }">
          {{ item.non_promo_items.length }}
        </template>
        <template #[`item.audit.updated_on`]="{ item }">
          {{ formatDateDisplay(item.audit.updated_on) }}
        </template>
        <!-- we are overriding the  'item' valeus  with the selectedAd values -->
        <template #[`item.ad_start_date`]="{}">
          {{ formatDateDisplay(selectedAd.ad_start_date) }}
        </template>
        <template #[`item.ad_end_date`]="{}">
          {{ formatDateDisplay(selectedAd.ad_end_date) }}
        </template>
        <template #[`item.ad_retail`]="{ item }">
          {{ `${item.count}/$${formatCurrencyDisplay(item.ad_retail)}` }}
        </template>
      </v-data-table>
    </v-container>
    <v-dialog v-if="!authorized" justify="center" :value="!authorized" persistent max-width="400">
      <v-card color=#fafafa>
        <section class="ma-0 pa-6">
          <!-- <v-row justify="center">
        </v-row> -->
          <v-row justify="center" dense>
            <v-icon x-large color="red">mdi-alert-decagram</v-icon>
            <span class='pa-4' style='text-align:center' justify-content='center'>
              You do not have permission to view {{ selectedAd.ad_name }}. If you believe you are seeing this message
              in error, contact Support.
            </span>
          </v-row>
        </section>
      </v-card>
    </v-dialog>
    <FeatureView
      v-if="modal"
      :value.sync="modal"
      :selectedAd="selectedAd"
      :selectedFeature="selectedFeature"
      :pages="pages"
      :pagePositions="pagePositions"
      :currentPage="currentPage"
      :getAdPageFeatures="getAdPageFeatures"
      :adFeatures="adFeatures"
      :adLayoutPages="adLayoutPages"
      :readonly="readonly"
      :adParty="adParty"
      :defaultParty="adParty"
      :parties="adGroups"
      @refresh="getAdPageFeatures"
      @closeFeatureView="closeFeatureView"
    />
    <v-fab-transition v-if="!readonly && !hideCreateFeature">
      <v-tooltip left>
        <template #activator="{ on }">
          <v-btn v-on="on" :style="floatingButton" absolute bottom right fab class="primary mr-6 mb-12"
            @click="modal = true">
            <v-icon>mdi-plus</v-icon>
          </v-btn>
        </template>
        <span>Create Feature</span>
      </v-tooltip>
    </v-fab-transition>
    <FeatureMassCopy 
      v-if="showMassCopy"
      :value.sync="showMassCopy" 
      :selectedAd="selectedAd" 
      :selectedFeatures="selectedFeatures"
      :adParty="adParty"
      :pages="pages"
      :currentPage="currentPage"
      @closeMassCopy="closeMassCopy">
    </FeatureMassCopy>
  </v-card>
</template>
<script>
import { displayHelpers } from '@/mixins/display-helpers'
import { userAccess } from '@/mixins/user-access'
import { format } from '@/mixins/format'
import { utils } from '@/mixins/utils'
export default {
  name: 'FeatureList',
  mixins: [displayHelpers, format, userAccess, utils],
  components: {
    AdActions: () => import('@/components/ads/ad-actions'),
    FeatureView: () => import('@/components/features/feature-view'),
    FeatureMassCopy: () => import('@/components/features/FeatureMassCopy.vue')
  },
  data() {
    return {
      adParty: {},
      authorized: true,
      component: '',
      filtering: false,
      loading: true,
      adFeatures: [],
      modal: false,
      currentPage: {},
      pagePositions: [],
      page: 1,
      pageSize: -1,
      pageSizes: [-1, 500, 1000],
      search: null,
      selectedAd: {},
      selectedFeature: {},
      selectedFeatures: [],
      sortBy: 'position.display_order',
      pageIndex: 0,
      parties: [],
      tableHeight: '',
      defaultParty: {},
      disableNext: false,
      disablePrevious: true,
      offset: true,
      upstream: [],
      adLayoutPages: [],
      showMassCopy: false
    }
  },
  computed: {
    hasAdLayout() {
      return Boolean(this.selectedAd.layout_id)
    },
    hideCreateFeature() {
      return this.$auth.tenant === 'alliance-retail-group' && this.hasAdLayout
    },
    adLayoutPageIds() {
      return (this.hasAdLayout && this.adLayoutPages.length) > 0 ? this.adLayoutPages.map(p => p.id) : []
    },
    pages () {
      const storedPages = this.$store.getters.adPages
      return this.adLayoutPageIds.length > 0 ? storedPages.filter(p => this.adLayoutPageIds.includes(p.id)) : storedPages
    },
    adGroups() {
      if (this.limitAccessByRelatedAdGroups) {
        return this.$store.getters.adGroups.filter(adGroup => this.userAdGroupIds.includes(adGroup?.party_id))
      } else {
        return this.$store.getters.adGroups
      }
    },
    breadcrumbs() {
      return [
        {
          text: `${this.adParty?.name || ''} Ads`,
          exact: true,
          disabled: false,
          to: { name: 'Ad Planner' }
        },
        {
          text: this.selectedAd.ad_name,
          exact: true,
          disabled: true
        },
        {
          text: this.moment(this.selectedAd.ad_start_date).format('M/D/YYYY') + ' - ' + this.moment(this.selectedAd.ad_end_date).format('M/D/YYYY'),
          exact: true,
          disabled: true
        },
      ]
    },
    readonly() {
      return this.selectedAd.status !== 'Draft' && this.selectedAd.status !== 'Rejected'
    },
    tableButtons() {
      return `
        height: 25px;
        width: 25px;
      `
    },
    floatingButton() {
      return `
        bottom: 25px;
        right: 5px; 
      `
    },
    featureHeaders() {
      return [
        { sortable: false, class: 'accent white--text', width: '3%', value: ((this.$auth.tenant === 'alliance-retail-group') ? 'data-table-select' : undefined), show: true },
        { text: 'Feature Position', align: 'left', sortable: true, filterable: true, value: 'position.name', class: 'accent white--text', show: true },
        { text: 'Feature Text', align: 'left', sortable: true, filterable: true, value: 'feature_text', class: 'accent white--text', show: true },
        { text: 'Promo Item Groups', align: 'center', sortable: true, filterable: false, value: 'promo_item_group_ids', class: 'accent white--text', show: true },
        { text: 'Non-Promo Items', align: 'center', sortable: true, filterable: false, value: 'non_promo_items', class: 'accent white--text', show: true },
        { text: 'Ad Retail', align: 'center', sortable: true, filterable: false, value: 'ad_retail', class: 'accent white--text', show: this.$auth.tenant === 'alliance-retail-group' },
        { text: 'Last Updated On', align: 'start', sortable: true, filterable: false, value: 'audit.updated_on', class: 'accent white--text', show: true },
      ].filter(fh => fh.show)
    }
  },
  watch: {
    adParty: {
      handler(newValue) {
        if (newValue) {
          sessionStorage.setItem('ad_party', JSON.stringify(newValue))
        }
      },
      deep: true
    },
    selectedAd: {
      deep: true,
      handler(newValue) {
        sessionStorage.setItem('selected_ad', JSON.stringify(newValue))
      }
    },
    modal: {
      handler(newValue, oldValue) {
        if (!newValue && oldValue) {
          this.selectedFeature = {}
          this.getAdPageFeatures()
        }
      }
    },
    search: {
      handler(newValue, oldValue) {
        if (!newValue && oldValue) {
          this.filtering = false
          this.getAdPageFeatures()
        }
      }
    }
  },
  async created() {
    this.tableHeight = window.innerHeight - 255
    if (this.$route.params.selectedAd) {
      this.selectedAd = this.$route.params.selectedAd
    } else {
      if (this.$route.query.id) {
        await this.getAd(this.$route.query.id)
      }
    }

    if (this.hasAdLayout) {
      await this.getAdLayoutPages()
    }

    if (this.$route.params.party) {
      this.adParty = this.$route.params.party
    } else if (sessionStorage.getItem('ad_party')) {
      this.adParty = JSON.parse(sessionStorage.getItem('ad_party'))
    } else {
      this.adParty = null
    }

    // If page thinks we're in a different ad group than the group this ad actually belongs to, reload the adgroup
    await this.verifySource()

    if (this.adGroups.length === 0) {
      await this.$store.dispatch('initParties')
    }
    this.getAdPages()
    this.$root.$on('refresh-features', () => this.getAdPageFeatures())
  },
  methods: {
    onResize() {
      this.tableHeight = window.innerHeight - 255
    },
    async getAdLayoutPages() {
      if (!this.selectedAd.layout_id) return
      try {
        const { data } = await this.$Features.getAdFeaturePages(this.selectedAd.layout_id)
        this.adLayoutPages = data
      } catch (err) {
        console.error(err)
      }
    },
    async getAd(ad_id) {
       try {
        const { data } = await this.$Ads.getAdById(ad_id)
        if (data) {
          this.selectedAd = data
        }
      } catch (e) {
        console.error(e)
        this.$store.dispatch('setSnackbar', { status: 'error', text: `Failed to Get Ad due to ${e?.response?.data?.message || e.message}` })
      }
    },
    getAdPages(currentPage) {
      this.currentPage = this.pages.find(page => page.constant === (currentPage?.constant || 'PAGE_1'))
      this.getAdPageFeatures()
    },
    async verifySource() {
      try {
        // Check that the party values match, and that the DC value exists
        if ((this.selectedAd.ad_party_id !== this.adParty?.party_id) || !this.adParty.distribution_center) {
          const res = await this.$Party.getPartyByID(this.selectedAd.ad_party_id)
          this.adParty = res.data
          this.adParty.party_id = this.adParty.id
          const dc_info = await this.getDistributionCenter(this.selectedAd.ad_party_id)
          this.adParty = { ...this.adParty, ...dc_info }
          sessionStorage.setItem('ad_party', JSON.stringify(this.adParty))
        }
      } catch(err) {
        console.error(err)
        this.$store.dispatch('setSnackbar', { status: 'error', text: `${err?.response?.data?.message || err.message}` })
      }
    },
    async getAdPageFeatures() {
      this.selectedFeatures = []
      this.loading = true
      try {
        const params = { offset: 0, limit: 300 }
        let { data } = await this.$Features.getAdPageFeatures(this.selectedAd.id, this.currentPage.id, params)
        this.adFeatures = data.map(m => {
          m.ad_retail = parseFloat(m.ad_retail).toFixed(2)
          if (this.$auth.tenant === 'alliance-retail-group') {
            m.isSelectable = (m.feature_text?.length > 0) 
              && ((m.non_promo_items?.length > 0) || (m.promo_item_group_ids?.length > 0))
          }
          return m
        })
      } catch (e) {
        console.error(e)
        this.$store.dispatch('setSnackbar', { status: 'error', text: `Failed to Get Page Positions due to ${e?.response?.data?.message || e.message}` })
      } finally {
        this.loading = false
      }
    },
    async getFilteredFeatures() {
      this.loading = true
      this.filtering = true
      this.adFeatures = []
      const requests = []
      const filteredResults = []
      this.pages.forEach(p => {
        requests.push(
          this.$Features.getAdPageFeatures(this.selectedAd.id, p.id)
            .then(res => {
              res.data.forEach(res => {
                if (this._.includes(res.position.name.toLowerCase(), this.search.toLowerCase()) || this._.includes(res.feature_text.toLowerCase(), this.search.toLowerCase())) {
                  filteredResults.push(res)
                }
              })
            })
        )
      })
      await Promise.allSettled(requests)
      this.adFeatures = filteredResults
      this.loading = false
    },
    viewFeature(feature) {
      this.selectedFeature = feature
      this.component = 'FeatureView'
      this.modal = true
    },
    closeFeatureView() {
      this.modal = false
    },
    closeMassCopy() {
      this.showMassCopy = false
      this.selectedFeatures = []
    }
  }
}
</script>

<style>
.active {
  box-shadow: 0px 2px 10px 3px rgba(120, 120, 120, 0.75);
  background: #385F73;
  color: white !important;
}

.theme--light.v-data-table .v-data-table-header th .v-data-table-header__icon {
  color: #fff !important;
}

.v-data-table-header .v-data-table__checkbox.v-simple-checkbox .v-input--selection-controls__input .v-icon {
  color: #fff !important;
}
</style>